import { EventLocationType, EventPreviewModel } from "client/Api/Hub/Events/eventViewer";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { EMPTY_ARRAY, isNotNil } from "client/Misc/utils";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import { ReactElement, useMemo } from "react";
import { CardProps, HubItemStatistic } from "../common";
import { CardBase } from "./CardBase";
import { NumberFormatter } from "client/Hooks";
import { useHubStatisticsFormatter } from "client/Features/Hub/useHubStatisticsFormatter";
import { ReactionStatistic, useReactionStatistic } from "client/Features/Reactions/useReactionStatistic";

export type EventCardProps = CardProps<EventPreviewModel>;

export const EventCard = (props: EventCardProps): ReactElement => {
	const {
		item,
		size,
		onOpen,
		hasTopBarBackdrop,
		shouldHideSecondaryInfo,
		timezone
	} = props;

	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();
	const reactionStatistic: ReactionStatistic = useReactionStatistic(item.reactionCounters);

	const statistics = useMemo(
		(): readonly HubItemStatistic[] => {
			const result: HubItemStatistic[] = [];
			const locationLabel: string | null = getEventLocationLabel(item);

			if (isNotNil(locationLabel)) {
				result.push({
					icon: "location-dot",
					text: locationLabel,
					trackingId: "location"
				});
			}

			result.push({
				icon: reactionStatistic.icon,
				text: statisticsFormatter(reactionStatistic.total),
				trackingId: "reaction-count"
			});

			if (item.areCommentsEnabled) {
				result.push({
					icon: "comment",
					text: statisticsFormatter(item.commentsCount),
					trackingId: "comment-count"
				});
			}

			return result;
		},
		[
			item,
			reactionStatistic.icon,
			reactionStatistic.total,
			statisticsFormatter
		]
	);

	const categoryNames = useMemo(
		(): readonly string[] => {
			return isNotNil(item.category)
				? [
					item.category
						.name
				]
				: EMPTY_ARRAY;
		},
		[item.category]
	);

	return (
		<CardBase
			itemKind={HubItemKind.Event}
			id={item.id}
			title={item.title}
			media={item.media}
			startDate={item.startDate}
			statistics={statistics}
			categoryNames={categoryNames}
			size={size}
			onOpen={onOpen}
			hasTopBarBackdrop={hasTopBarBackdrop}
			shouldHideSecondaryInfo={shouldHideSecondaryInfo}
			timezone={timezone}
			isNew={item.isNew}
		/>
	);
};
EventCard.displayName = "EventCard";

export function getEventLocationLabel(item: EventPreviewModel): string | null {
	if (item.locationType === EventLocationType.Unknown) {
		return null;
	}

	switch (item.locationType) {
		case EventLocationType.Hybrid:
			return localize("_Hybrid");
		case EventLocationType.Online:
			return localize("_Online");
		case EventLocationType.Physical:
			return item.location;
		default:
			never(item.locationType);
	}
}